import React, { useState } from 'react';
import { Typography } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { useAudio } from './AudioRef';

/**
 * 标题 + 中文指令 + 小喇叭 按钮组件
 * 复用于所有页面的标题栏
 *
 * props:
 *   title          英文标题（显示原文字）
 *   titleAudio     英文标题音频路径（可选）
 *   titleCn        中文翻译（可选）
 *   instruction    中文指令（如"看视频"）
 *   instructionAudio  中文指令音频路径
 *   inlineCn       true: 中文翻译与英文标题同行显示，配音在翻译右边
 */
function InstructionTitle({ title, titleAudio, titleCn, instruction, instructionAudio, inlineCn = false }) {
  const [titlePlaying, setTitlePlaying] = useState(false);
  const [instrPlaying, setInstrPlaying] = useState(false);
  const audio = useAudio();

  // 组件卸载时停止音频
  React.useEffect(() => {
    return () => audio.stop();
  }, []);

  const playTitleAudio = () => {
    if (!titleAudio) return;
    audio.play(titleAudio, () => setTitlePlaying(false));
    setTitlePlaying(true);
  };

  const playInstruction = () => {
    if (!instructionAudio) return;
    audio.play(instructionAudio, () => setInstrPlaying(false));
    setInstrPlaying(true);
  };

  // 同行模式：英文标题 + 中文翻译（带配音） 在同一行
  if (inlineCn) {
    return (
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        {/* 英文标题 + 中文翻译（同行） */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: instruction ? 4 : 0 }}>
          <Typography variant="h5" component="span" sx={{ fontWeight: 700, color: '#333', fontSize: '1.2rem', lineHeight: 1.3 }}>
            {title}
          </Typography>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600, color: '#FF9F1C', fontSize: '1.1rem', lineHeight: 1.3 }}>
            {titleCn}
          </Typography>
          {instructionAudio && (
            <button
              className={`phonics-audio-btn phonics-instruction-btn ${instrPlaying ? 'playing' : ''}`}
              onClick={playInstruction}
              title={`播放：${titleCn}`}
            >
              <VolumeUpIcon sx={{ fontSize: 16 }} />
            </button>
          )}
        </div>

        {/* 中文指令行（可选） */}
        {instruction && !instructionAudio && (
          <Typography variant="body1" component="div" sx={{ fontSize: '0.95rem', color: '#666' }}>
            {instruction}
          </Typography>
        )}
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center', marginBottom: 12 }}>
      {/* 英文标题行 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: titleCn ? 4 : 0 }}>
        <Typography variant="h5" component="span" sx={{ fontWeight: 700, color: '#333', fontSize: '1.2rem', lineHeight: 1.3 }}>
          {title}
        </Typography>
        {titleAudio && (
          <button
            className={`phonics-audio-btn phonics-instruction-btn ${titlePlaying ? 'playing' : ''}`}
            onClick={playTitleAudio}
            title={`Play: ${title}`}
          >
            <VolumeUpIcon sx={{ fontSize: 16 }} />
          </button>
        )}
      </div>

      {/* 中文翻译（可选） */}
      {titleCn && (
        <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#FF9F1C', fontSize: '1rem', mb: 0.5 }}>
          {titleCn}
        </Typography>
      )}

      {/* 中文指令行（可选） */}
      {instruction && (
        <Typography variant="body1" component="div" sx={{ fontSize: '0.95rem', color: '#666' }}>
          {instruction}
          {instructionAudio && (
            <button
              className={`phonics-audio-btn phonics-instruction-btn ${instrPlaying ? 'playing' : ''}`}
              onClick={playInstruction}
              title={`播放：${instruction}`}
              style={{ marginLeft: 6 }}
            >
              <VolumeUpIcon sx={{ fontSize: 14 }} />
            </button>
          )}
        </Typography>
      )}
    </div>
  );
}

export default InstructionTitle;
